import type { RunnerMode, AgentMode, TaskContext } from "@project/shared";
import { buildPackRunnerSystemPrompt } from "./pack-runner-prompt.js";
import { buildModePrompt } from "./mode-prompt.js";

function formatProjectAgentLine(pa: NonNullable<TaskContext["projectAgents"]>[number]): string {
  const role = pa.role ? `role: ${pa.role}` : "role: unassigned";
  const sp =
    pa.storyPoints === null || pa.storyPoints === undefined
      ? ""
      : `, story points: ${pa.storyPoints}`;
  return `- ${pa.agent.name} (${role}${sp})`;
}

function buildPmPreamble(context: TaskContext): string[] {
  const parts = [
    `You are an AI project manager helping to plan tasks for the "${context.title}" project.`,
    `You are running locally with full access to the repository.`,
    `You can search code (grep, glob), read files, and run shell commands (e.g. git log, git diff). Search to locate relevant code before reading files. You cannot write or edit files.`,
    `\nEnvironment (ready, no setup required):`,
    `- Repository is cloned at your current working directory.`,
    `- The shell cwd resets between Bash calls — always use absolute paths (or \`git -C\`); never spend a tool call on a bare \`cd\`.`,
    `- You can read files and run git commands to understand the codebase before writing task plans.`,
    `- Check the dev branch (e.g. run: git fetch && git checkout dev || git checkout main) to understand the current state of the codebase that agents will branch off of.`,
    `\nWorkflow:`,
    `- You can draft and iterate on plans in .claude/plans/*.md, but files on disk are NOT synced to the task.`,
    `- Save the plan to the task with update_task_plan — this is the only way the plan is persisted.`,
    `- After saving the plan, call post_to_chat with a short summary for the team (your turn output is NOT posted to chat — they only see what you post), then end your turn. Do NOT attempt to execute the plan yourself.`,
    `- A separate task agent will handle execution after the team reviews and approves your plan.`,
  ];
  if (context.isParentTask) {
    parts.push(
      `\nYou are the Project Manager for this set of tasks.`,
      `This task has child tasks (subtasks) that are tracked on the board.`,
      `Your role is to coordinate, plan, and manage the subtasks — not to write code directly.`,
      `Use the subtask tools (create_subtask, update_subtask, list_subtasks) to manage work breakdown.`,
    );
  }
  if (context.projectAgents && context.projectAgents.length > 0) {
    parts.push(`\nProject Agents:`);
    for (const pa of context.projectAgents) {
      parts.push(formatProjectAgentLine(pa));
    }
  }
  return parts;
}

function buildActivePreamble(context: TaskContext, workspaceDir: string): string[] {
  return [
    `You are an AI project manager in ACTIVE mode for the "${context.title}" project.`,
    `You have direct coding access to the repository at ${workspaceDir}.`,
    `You can edit files, run tests, and make commits.`,
    `You still have access to all PM tools (subtasks, update_task_plan, chat).`,
    `\nEnvironment (ready, no setup required):`,
    `- Repository is cloned at your current working directory.`,
    `- You can read, write, and edit files directly.`,
    `- You can run shell commands including git, build tools, and test runners.`,
    `- The shell cwd resets between Bash calls — always use absolute paths (or \`git -C\`, \`bun run --cwd\`); never spend a tool call on a bare \`cd\`.`,
    context.githubBranch ? `- You are working on branch: \`${context.githubBranch}\`` : "",
    `\nSafety rules:`,
    `- Stay within the project directory (${workspaceDir}).`,
    `- Do NOT run \`git push --force\` or \`git reset --hard\`. Use \`--force-with-lease\` if needed.`,
    `- Do NOT delete \`.env\` files or modify \`node_modules\`.`,
    `- Do NOT run destructive commands like \`rm -rf /\`.`,
    `\nWorkflow:`,
    `- You can make code changes, fix bugs, run tests, and commit directly.`,
    `- When done with changes, summarize what you did in your reply.`,
    `- If you toggled into active mode temporarily, mention when you're done so the team can switch you back to planning mode.`,
  ].filter(Boolean);
}

function buildTaskAgentPreamble(context: TaskContext): string[] {
  return [
    `You are an AI agent working on a task for the "${context.title}" project.`,
    `You are running inside a GitHub Codespace with full access to the repository.`,
    `\nEnvironment (fully ready — do NOT verify or set up):`,
    `- Repository is cloned at your current working directory.`,
    `- Branch \`${context.githubBranch}\` is already checked out.`,
    `- All dependencies are installed, database is migrated, and the dev server is running.`,
    `- Git is configured. Commit and push directly to this branch.`,
    `- The shell cwd resets between Bash calls — always use absolute paths (or \`git -C\`, \`bun run --cwd\`); never spend a tool call on a bare \`cd\`.`,
    `- When a build/lint/test run fails, capture its output to a file once and grep the file — never re-run the suite just to re-filter the same output.`,
    `- Waiting on long-running commands: if a gate finishes in under ~2 minutes, run it in the foreground with a timeout. Anything longer: launch it with run_in_background and STOP — a completion notification arrives when it finishes. Never busy-wait with sleep/pgrep/tail loops (foreground shell commands are killed at ~2 minutes), and never re-run the suite to escape a wait that looks stalled.`,
    `- Deferred tools have no schema loaded until fetched — before first use, load it via ToolSearch (query "select:<ToolName>", e.g. "select:Monitor"); never guess a deferred tool's parameters.`,
    `- The \`gh\` CLI may not be installed — use the mcp__conveyor__* tools for PR and task state.`,
    `- Read a file before your first Write/Edit to it, and batch multiple changes to the same file into a single call instead of many sequential edits.`,
    `\nIMPORTANT — Skip all environment verification. Do NOT run any of the following:`,
    `- bun/npm install, pip install, or any dependency installation`,
    `- bun build, bun lint, bun test, bun typecheck, or any build/check commands as a "first step"`,
    `- bun db:generate, bun db:push, prisma migrate, or any database setup`,
    `- bun dev, npm start, or any dev server startup commands`,
    `- pwd, ls, echo, or exploratory shell commands to "check" the environment`,
    `Only run these if you encounter a specific error that requires it.`,
    `Start reading the task plan and writing code immediately.`,
    `\nGit safety — STRICT rules:`,
    `- NEVER run \`git checkout main\`, \`git checkout dev\`, or switch to any branch other than \`${context.githubBranch}\`.`,
    `- NEVER create new branches (no \`git checkout -b\`, \`git switch -c\`, etc.).`,
    `- This branch was created from \`${context.baseBranch}\`. PRs will automatically target that branch.`,
    `- If \`git push\` fails with "non-fast-forward", run \`git push --force-with-lease origin ${context.githubBranch}\`. This branch is exclusively yours — force-with-lease is safe.`,
  ];
}

export function buildSystemPrompt(
  mode: RunnerMode | undefined,
  context: TaskContext,
  config: { instructions: string; workspaceDir: string; isAuto?: boolean },
  setupLog: string[],
  agentMode?: AgentMode | null,
): string {
  const isPm = mode === "pm";
  const isPmActive = isPm && agentMode === "building";
  // RunnerMode "pack" is the first-class gate (parent cards on v3 launch with
  // session mode "pack" → CONVEYOR_MODE=pack). The pm clause is the legacy
  // gate from the deleted ProjectRunner surface — kept harmless.
  const isPackRunner = mode === "pack" || (isPm && !!config.isAuto && !!context.isParentTask);

  if (isPackRunner) {
    return buildPackRunnerSystemPrompt(context, config, setupLog);
  }

  const parts = isPmActive
    ? buildActivePreamble(context, config.workspaceDir)
    : isPm
      ? buildPmPreamble(context)
      : buildTaskAgentPreamble(context);

  if (setupLog.length > 0) {
    parts.push(
      `\nEnvironment setup log (already executed before you started — proof that setup succeeded):`,
      "```",
      ...setupLog,
      "```",
    );
  }

  if (context.agentInstructions) {
    parts.push(`\nAgent Instructions:\n${context.agentInstructions}`);
  }
  if (config.instructions) {
    parts.push(`\nAdditional Instructions:\n${config.instructions}`);
  }
  parts.push(
    `\nYour turn output appears ONLY in the live agent terminal — it is NOT posted to the task chat. The team does NOT see your replies unless you post them.`,
    `Use post_to_chat (omit task_id → your own task's chat) to share meaningful status as you work, a summary when you finish, and any blocker or question you need a human to answer.`,
    `Pass task_id only to message a DIFFERENT task's chat (e.g. a parent task via get_task).`,
    `Use read_task_chat only if you need to re-read earlier messages beyond the chat context above.`,
    `\nIf a Conveyor tool call fails or reports the MCP server is unavailable/disconnected, this is almost always a transient socket reconnect — simply RETRY the same call and it will succeed once the connection re-establishes. Do NOT stop work, go idle, or treat a tool error as a reason to end your turn. Keep making progress on the code; only post_to_chat to ask for a human if a tool keeps failing across several retries over a sustained period.`,
  );
  if (!isPm || isPmActive) {
    parts.push(
      `Use the mcp__conveyor__create_pull_request tool to open PRs — it automatically stages, commits, and pushes changes before creating the PR. Do NOT use gh CLI or shell commands for PR creation.`,
    );
  }

  const modePrompt = buildModePrompt(agentMode, context, mode);
  if (modePrompt) {
    parts.push(modePrompt);
  }

  return parts.join("\n");
}
